import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  BankAccountDetails,
  BankAccountEditFormValues,
} from '@/types/bankAccounts';
import { useForm } from 'react-hook-form';
import { SVAutocomplete, SVForm } from '../../core';
import FormSubmissionButtons from '@/components/FormSubmissionButtons';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { SelectOption } from '@/types/inputs';
import { Card, Stack, Typography } from '@mui/material';
import BankAccountEditFormFees from './BankAccountEditFormFees';
import BankAccountEditFormControls from './BankAccountEditFormControls';
import { useUpdateBankAccount } from '@/hooks/requests/useBankAccounts';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';

type BankAccountEditFormProps = {
  accountDetails: BankAccountDetails;
  teammatesList: SelectOption[];
};

export default function BankAccountEditForm({
  accountDetails,
  teammatesList,
}: BankAccountEditFormProps) {
  const navigate = useNavigate();
  const { isStaff } = useAuthUser();
  const { updateBankAccount, isUpdateBankAccountPending } =
    useUpdateBankAccount();

  const useCompanyIncomingDomesticFee =
    Number(accountDetails.fees.incomingDomestic.value) <= 0;
  const useCompanyOutgoingDomesticFee =
    Number(accountDetails.fees.outgoingDomestic.value) <= 0;
  const useCompanySavingsAccountFee =
    Number(accountDetails.fees.monthlySavingAccount.value) <= 0;
  const formMethods = useForm<BankAccountEditFormValues>({
    defaultValues: {
      isStaff,
      uuid: accountDetails.uuid,
      advisor: accountDetails.advisor.uuid,
      incomingDomesticFee: {
        useCompanySetting: useCompanyIncomingDomesticFee,
        fee: useCompanyIncomingDomesticFee
          ? accountDetails.company.feeIncomingDomestic?.value
          : accountDetails.fees.incomingDomestic.value,
      },
      outgoingDomesticFee: {
        useCompanySetting: useCompanyOutgoingDomesticFee,
        fee: useCompanyOutgoingDomesticFee
          ? accountDetails.company.feeOutcomeDomestic?.value
          : accountDetails.fees.outgoingDomestic.value,
      },
      savingsAccountFee: {
        useCompanySetting: useCompanySavingsAccountFee,
        fee: useCompanySavingsAccountFee
          ? accountDetails.company.feeMonthlySavingAccount?.value
          : accountDetails.fees.monthlySavingAccount.value,
      },
      checkIssuingControls: {
        setLimit: accountDetails.isCheckIssuanceOverrideEnabled,
        limit: accountDetails.checkIssuanceOverrideLimit?.value || '',
        expiration: accountDetails.checkIssuanceOverrideLimitExpiredAt || '',
      },
      wireControls: {
        setLimit: accountDetails.isWireOutOverrideEnabled,
        limit: accountDetails.wireOutOverrideLimit?.value || '',
        expiration: accountDetails.wireOutOverrideLimitExpiredAt || '',
      },
      mobileCheckDepositControls: {
        setLimit: accountDetails.isCheckDepositOverrideEnabled,
        limit: accountDetails.checkDepositOverrideLimit?.value || '',
        expiration: accountDetails.checkDepositOverrideLimitExpiredAt || '',
      },
      achCreditControls: {
        setLimit: accountDetails.isAchCreditOverrideEnabled,
        limit: accountDetails.achCreditOverrideLimit?.value || '',
        expiration: accountDetails.achCreditOverrideLimitExpiredAt || '',
      },
      achDebitControls: {
        setLimit: accountDetails.isAchDebitOverrideEnabled,
        limit: accountDetails.achDebitOverrideLimit?.value || '',
        expiration: accountDetails.achDebitOverrideLimitExpiredAt || '',
      },
    },
    mode: 'onBlur',
  });

  async function handleSubmit(data: BankAccountEditFormValues) {
    try {
      await updateBankAccount(data);
      showToast({
        type: 'success',
        data: { content: 'Bank account has been successfully updated' },
      });
      navigate(`${ROUTES.BANK_ACCOUNTS}/${accountDetails.uuid}`);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <SVForm methods={formMethods} onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Card>
          <Typography variant="h5" mb={2}>
            Basic Info
          </Typography>
          <SVAutocomplete
            name="advisor"
            label="Advisor"
            options={teammatesList}
            rules={{ required: true }}
          />
        </Card>
        {isStaff && (
          <>
            <BankAccountEditFormFees />
            <BankAccountEditFormControls
              title="Check Issuing"
              setLimitCheckboxName="checkIssuingControls.setLimit"
              limitName="checkIssuingControls.limit"
              expirationName="checkIssuingControls.expiration"
            />
            <BankAccountEditFormControls
              title="Wire"
              setLimitCheckboxName="wireControls.setLimit"
              limitName="wireControls.limit"
              expirationName="wireControls.expiration"
            />
            <BankAccountEditFormControls
              title="Mobile Check Deposit"
              setLimitCheckboxName="mobileCheckDepositControls.setLimit"
              limitName="mobileCheckDepositControls.limit"
              expirationName="mobileCheckDepositControls.expiration"
            />
            <BankAccountEditFormControls
              title="ACH Credit"
              setLimitCheckboxName="achCreditControls.setLimit"
              limitName="achCreditControls.limit"
              expirationName="achCreditControls.expiration"
            />
            <BankAccountEditFormControls
              title="ACH Debit"
              setLimitCheckboxName="achDebitControls.setLimit"
              limitName="achDebitControls.limit"
              expirationName="achDebitControls.expiration"
            />
          </>
        )}
        <FormSubmissionButtons
          isLoading={isUpdateBankAccountPending}
          onLeave={() =>
            navigate(`${ROUTES.BANK_ACCOUNTS}/${accountDetails.uuid}`)
          }
        />
      </Stack>
    </SVForm>
  );
}
